import { Button } from '../../components/ui/button';
import { cn } from '../../lib/utils';
import type { BalanceSheetRow, ProfitAndLossReportLine, TrialBalanceReportRow } from '../../services/types';

type StatementMode = 'balance-sheet' | 'profit-and-loss' | 'trial-balance';
type StatementRow = BalanceSheetRow | ProfitAndLossReportLine | TrialBalanceReportRow;

interface LedgerTarget {
    id: number;
    code: string;
    name: string;
    type: string;
}

interface FinancialStatementTableProps {
    mode: StatementMode;
    rows: StatementRow[];
    total: number;
    limit: number;
    offset: number;
    loading?: boolean;
    onPageChange: (nextOffset: number) => void;
    onPageSizeChange: (nextLimit: number) => void;
    onAccountClick: (account: LedgerTarget) => void;
}

function formatMoney(value: number | null | undefined) {
    return new Intl.NumberFormat('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    }).format(Number(value || 0));
}

function getLabel(row: StatementRow) {
    if ('display_label' in row && row.display_label) return row.display_label;
    if ('name' in row && row.name) return row.name;
    if ('account_name' in row && row.account_name) return row.account_name;
    return '';
}

function getIndent(row: StatementRow) {
    if ('indent_level' in row && typeof row.indent_level === 'number') {
        return Math.max(0, row.indent_level) * 18;
    }
    return 0;
}

function isRowClickable(row: StatementRow) {
    if (!('account_id' in row) || !row.account_id) return false;
    if ('row_type' in row && row.row_type !== 'account') return false;
    if ('is_clickable' in row && row.is_clickable === false) return false;
    return true;
}

function toLedgerTarget(mode: StatementMode, row: StatementRow): LedgerTarget | null {
    if (!('account_id' in row) || !row.account_id) return null;
    return {
        id: row.account_id,
        code: row.account_code || '',
        name: row.account_name || getLabel(row),
        type: mode === 'balance-sheet' ? ('section' in row ? row.section : 'Account') : ('account_type' in row ? row.account_type : 'Account'),
    };
}

function renderStatementCell(mode: StatementMode, row: StatementRow, onAccountClick: (account: LedgerTarget) => void) {
    const label = getLabel(row);
    const indent = getIndent(row);
    const clickable = isRowClickable(row);
    const ledgerTarget = toLedgerTarget(mode, row);
    const rowType = 'row_type' in row ? row.row_type : 'account';
    const rowTone = rowType === 'section_header'
        ? 'text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500'
        : rowType === 'subgroup_header'
            ? 'text-sm font-semibold text-slate-800'
            : rowType === 'subtotal' || rowType === 'section_total' || rowType === 'calculated_total'
                ? 'text-sm font-semibold text-slate-900'
                : 'text-sm text-slate-700';

    if (!clickable || !ledgerTarget) {
        return (
            <div className={cn(rowTone, rowType === 'account' && 'font-medium text-slate-900')} style={{ paddingLeft: `${indent}px` }}>
                {label || '-'}
            </div>
        );
    }

    return (
        <button
            type="button"
            className={cn('text-left hover:underline', rowTone, rowType === 'account' && 'font-medium text-blue-700')}
            style={{ paddingLeft: `${indent}px` }}
            onClick={() => onAccountClick(ledgerTarget)}
        >
            {label || '-'}
        </button>
    );
}

function getStatementTypeLabel(mode: StatementMode, row: BalanceSheetRow | ProfitAndLossReportLine) {
    if (row.row_type === 'section_total' || row.row_type === 'subtotal' || row.row_type === 'calculated_total') {
        return 'Total';
    }
    if (row.row_type === 'section_header') return 'Section';
    if (row.row_type === 'subgroup_header') return 'Group';
    if (mode === 'profit-and-loss' && 'account_type' in row && row.account_type) {
        return row.account_type;
    }
    return 'Account';
}

export function FinancialStatementTable({
    mode,
    rows,
    total,
    limit,
    offset,
    loading = false,
    onPageChange,
    onPageSizeChange,
    onAccountClick,
}: FinancialStatementTableProps) {
    const start = total === 0 ? 0 : offset + 1;
    const end = Math.min(offset + rows.length, total);
    const canGoBack = offset > 0;
    const canGoNext = offset + limit < total;

    if (mode === 'trial-balance') {
        const trialBalanceRows = rows as TrialBalanceReportRow[];
        return (
            <div className="space-y-3">
                <div className="overflow-x-auto rounded-2xl border border-slate-200 bg-white">
                    <table className="w-full min-w-[840px] text-left text-sm">
                        <thead className="border-b border-slate-200 bg-slate-50">
                            <tr>
                                <th className="px-4 py-3 font-medium text-slate-500">Code</th>
                                <th className="px-4 py-3 font-medium text-slate-500">Account Name</th>
                                <th className="px-4 py-3 font-medium text-slate-500">Type</th>
                                <th className="px-4 py-3 text-right font-medium text-slate-500">Debit</th>
                                <th className="px-4 py-3 text-right font-medium text-slate-500">Credit</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr>
                                    <td colSpan={5} className="px-4 py-8 text-center text-slate-500">
                                        Loading statement...
                                    </td>
                                </tr>
                            ) : trialBalanceRows.length === 0 ? (
                                <tr>
                                    <td colSpan={5} className="px-4 py-8 text-center text-slate-500">
                                        No rows match the current filters.
                                    </td>
                                </tr>
                            ) : (
                                trialBalanceRows.map((row) => {
                                    const ledgerTarget = toLedgerTarget(mode, row);
                                    return (
                                        <tr key={row.row_id} className="border-b border-slate-100 last:border-b-0">
                                            <td className="px-4 py-3 font-medium text-slate-900">{row.account_code || '-'}</td>
                                            <td className="px-4 py-3">
                                                {ledgerTarget ? (
                                                    <button
                                                        type="button"
                                                        className="font-medium text-blue-700 hover:underline"
                                                        onClick={() => onAccountClick(ledgerTarget)}
                                                    >
                                                        {row.account_name}
                                                    </button>
                                                ) : (
                                                    <span className="font-medium text-slate-900">{row.account_name}</span>
                                                )}
                                            </td>
                                            <td className="px-4 py-3 text-slate-600">{row.account_type || '-'}</td>
                                            <td className="px-4 py-3 text-right tabular-nums text-slate-700">{formatMoney(row.debit)}</td>
                                            <td className="px-4 py-3 text-right tabular-nums text-slate-700">{formatMoney(row.credit)}</td>
                                        </tr>
                                    );
                                })
                            )}
                        </tbody>
                    </table>
                </div>

                <div className="flex flex-col gap-3 rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-600 sm:flex-row sm:items-center sm:justify-between">
                    <div className="flex items-center gap-3">
                        <span className="font-medium text-slate-800">
                            {start}-{end} of {total}
                        </span>
                        <label className="flex items-center gap-2">
                            <span className="text-xs uppercase tracking-wide text-slate-500">Page size</span>
                            <select
                                className="h-9 rounded-md border border-slate-200 bg-white px-3 text-sm"
                                value={String(limit)}
                                onChange={(event) => onPageSizeChange(Number(event.target.value))}
                            >
                                <option value="25">25</option>
                                <option value="50">50</option>
                                <option value="100">100</option>
                            </select>
                        </label>
                    </div>

                    <div className="flex items-center gap-2">
                        <Button variant="outline" size="sm" onClick={() => onPageChange(Math.max(0, offset - limit))} disabled={!canGoBack || loading}>
                            Previous
                        </Button>
                        <Button variant="outline" size="sm" onClick={() => onPageChange(offset + limit)} disabled={!canGoNext || loading}>
                            Next
                        </Button>
                    </div>
                </div>
            </div>
        );
    }

    const statementRows = rows as Array<BalanceSheetRow | ProfitAndLossReportLine>;

    return (
        <div className="space-y-3">
            <div className="overflow-x-auto rounded-2xl border border-slate-200 bg-white">
                <table className="w-full min-w-[980px] text-left text-sm">
                    <thead className="border-b border-slate-200 bg-slate-50">
                        <tr>
                            <th className="px-4 py-3 font-medium text-slate-500">Section</th>
                            <th className="px-4 py-3 font-medium text-slate-500">Code</th>
                            <th className="px-4 py-3 font-medium text-slate-500">Account</th>
                            <th className="px-4 py-3 font-medium text-slate-500">Type</th>
                            <th className="px-4 py-3 text-right font-medium text-slate-500">
                                {mode === 'profit-and-loss' ? 'Amount' : 'Balance'}
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? (
                            <tr>
                                <td colSpan={5} className="px-4 py-8 text-center text-slate-500">
                                    Loading statement...
                                </td>
                            </tr>
                        ) : statementRows.length === 0 ? (
                            <tr>
                                <td colSpan={5} className="px-4 py-8 text-center text-slate-500">
                                    No rows match the current filters.
                                </td>
                            </tr>
                        ) : (
                            statementRows.map((row) => {
                                const rowType = row.row_type;
                                const highlight = rowType === 'section_header'
                                    ? 'bg-slate-50'
                                    : rowType === 'subtotal' || rowType === 'section_total' || rowType === 'calculated_total'
                                        ? 'bg-slate-50/70'
                                        : '';
                                return (
                                    <tr key={row.row_id} className={cn('border-b border-slate-100 last:border-b-0', highlight)}>
                                        <td className="px-4 py-3 text-slate-600">{row.section || '-'}</td>
                                        <td className="px-4 py-3 font-medium text-slate-500">{rowType === 'account' ? row.account_code || '-' : ''}</td>
                                        <td className="px-4 py-3">{renderStatementCell(mode, row, onAccountClick)}</td>
                                        <td className="px-4 py-3 text-slate-600">{getStatementTypeLabel(mode, row)}</td>
                                        <td className={cn(
                                            'px-4 py-3 text-right tabular-nums',
                                            rowType === 'subtotal' || rowType === 'section_total' || rowType === 'calculated_total'
                                                ? 'font-semibold text-slate-900'
                                                : rowType === 'section_header' || rowType === 'subgroup_header'
                                                    ? 'text-slate-400'
                                                    : 'text-slate-700'
                                        )}>
                                            {rowType === 'section_header' || rowType === 'subgroup_header' ? '' : formatMoney(row.balance)}
                                        </td>
                                    </tr>
                                );
                            })
                        )}
                    </tbody>
                </table>
            </div>

            <div className="flex flex-col gap-3 rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-600 sm:flex-row sm:items-center sm:justify-between">
                <div className="flex items-center gap-3">
                    <span className="font-medium text-slate-800">
                        {start}-{end} of {total}
                    </span>
                    <label className="flex items-center gap-2">
                        <span className="text-xs uppercase tracking-wide text-slate-500">Page size</span>
                        <select
                            className="h-9 rounded-md border border-slate-200 bg-white px-3 text-sm"
                            value={String(limit)}
                            onChange={(event) => onPageSizeChange(Number(event.target.value))}
                        >
                            <option value="25">25</option>
                            <option value="50">50</option>
                            <option value="100">100</option>
                        </select>
                    </label>
                </div>

                <div className="flex items-center gap-2">
                    <Button variant="outline" size="sm" onClick={() => onPageChange(Math.max(0, offset - limit))} disabled={!canGoBack || loading}>
                        Previous
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => onPageChange(offset + limit)} disabled={!canGoNext || loading}>
                        Next
                    </Button>
                </div>
            </div>
        </div>
    );
}
